// 검색 모달(브랜드명·스타일번호·상품코드) 후보 — 1.0 MA02 서치헬퍼(BRD_NM·STL_NO·GDS_CD_SearchForm) 이관.
//  BE: GET /api/v1/master/products/{brand-names,style-numbers,goods-codes} (LOGISPRD-9955·14074) — 무페이징 distinct.
//  - brandName·itemName = contains, styleNumber = eq(1.0 MOMS_FILTER_INFO STL_NO=EQUAL 과 같다)
//  - brand-names·style-numbers 는 BE 가 1,000행에서 끊고, goods-codes 는 상한이 없다(조건 필수 판단은 index.tsx)
import qs from 'qs'
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import request from '@/utils/request'

export type HelperKind = 'brand-names' | 'style-numbers' | 'goods-codes'

/** 화주 축(조회영역) + 모달 조건. 비어 있는 값은 쿼리에서 뺀다. */
export interface HelperOptionParams {
  shipperCode?: string
  serviceTypes?: string[]
  brandName?: string
  itemName?: string
  styleNumber?: string
}

interface HelperOptionItem {
  brandName?: string | null
  styleNumber?: string | null
  itemCode?: string | null
  itemName?: string | null
}

/** 모달 표 행 — 컬럼 키(BRD_NM·STL_NO·GDS_CD·GDS_NM) + 행 key(distinct 조합). */
export type HelperOptionRow = Record<string, string>

const toRow = (i: HelperOptionItem): HelperOptionRow => {
  const row: HelperOptionRow = {
    BRD_NM: i.brandName ?? '',
    STL_NO: i.styleNumber ?? '',
    GDS_CD: i.itemCode ?? '',
    GDS_NM: i.itemName ?? '',
  }
  return { key: Object.values(row).join('\u0001'), ...row }
}

const fetchHelperOptions = async (kind: HelperKind, params: HelperOptionParams): Promise<HelperOptionRow[]> => {
  // 자유입력 검색어라 인코딩한다 — 공용 Utils.qsStringify 는 encode:false 라 'H&M'·'#'·'+' 가 다른 값으로 간다(재고 화면 toRepeatQuery 와 같은 이유).
  //  빈 문자열은 뺀다(styleNumber 는 정확일치라 빈 값이 걸리면 0건). serviceTypes 는 repeat 형식(BE List 바인딩).
  const query = qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true, filter: (_, v: unknown) => (v === '' ? undefined : v) })
  const res = await request.get<ApiResponseV2<{ items: HelperOptionItem[] }>>(`/api/v1/master/products/${kind}${query ? `?${query}` : ''}`)
  return (res.data?.items ?? []).map(toRow)
}

export const productHelperQueryKeys = {
  all: ['MA02_HELPER_OPTIONS'] as const,
  list: (kind: HelperKind, params: HelperOptionParams, seq: number) => [...productHelperQueryKeys.all, kind, params, seq] as const,
}

/** seq — 모달 [조회] 순번. 같은 조건이어도 [조회]마다 키가 달라져 다시 받는다. */
export const useProductHelperOptions = (
  kind: HelperKind,
  params: HelperOptionParams,
  seq: number,
  enabled: boolean,
): UseQueryResult<HelperOptionRow[], Error> =>
  useQuery({
    queryKey: productHelperQueryKeys.list(kind, params, seq),
    queryFn: () => fetchHelperOptions(kind, params),
    enabled,
    meta: { noErrorMessage: true }, // 실패 안내는 화면이 새 디자인 알럿으로 한 번 낸다(usePboQueryErrorToast → notice.failure — 옛 전역 알럿 대신)
  })
